import { IList } from 'annotation-tools/utils';
import React, { FC, memo } from 'react';
import styled from 'styled-components';
import { viewTexts } from '../../../constants/view-texts';
import { IProjectSummaryType } from '../../../types/record/project-summary';
import { CountResult } from '../../shared/count-result';
import { MuiButton } from '../../shared/form-element';
import { MessageArea } from '../../shared/message-area';
import { H3 } from '../../shared/styled/heading';
import { Root } from '../../shared/styled/root';

const vt = viewTexts.pages.projectDetail;

const MessageAreaWrapper = styled.div`
  padding: 20px 0;
`;

const Body = styled.div`
  padding: 10px;
`;

const Section = styled.div`
  padding: 20px 0;
`;

const Description = styled.div`
  padding: 5px;
`;

const ButtonListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 5px;

  & > * {
    display: block;
    margin-right: 5px;
    margin-bottom: 5px;
  }
`;

const CountResultWrapper = styled.div`
  padding: 5px;
  display: flex;
`;

const CountResultShrinkWrapper = styled.div`
  padding: 5px;
  flex: 0 0 500px;
`;

const noop = () => undefined;

interface Props {
  projectIsApproved: boolean;
  projectAuthorName: string;
  projectSummary: IList<IProjectSummaryType>;
  projectImagesSummaryDescription: string;
  noImageRegistered: boolean;
  projectIsApprovable: boolean;
  onEditProjectImagesClick: () => void;
  onApproveResultClick: () => void;
  onUnapproveClick: () => void;
  onDownloadPdfClick: () => void;
  onDownloadCsvFileClick: () => void;
}

export const ProjectDetailView: FC<Props> = memo(
  ({
    projectIsApproved,
    projectAuthorName,
    projectSummary,
    projectImagesSummaryDescription,
    noImageRegistered,
    projectIsApprovable,
    onEditProjectImagesClick,
    onApproveResultClick,
    onUnapproveClick,
    onDownloadPdfClick,
    onDownloadCsvFileClick
  }: Readonly<Props>) => (
    <Root>
      <Description>{vt.description}</Description>
      {projectIsApproved ? (
        <MessageAreaWrapper>
          <MessageArea status='default' messages={vt.message.readonlyProject} />
        </MessageAreaWrapper>
      ) : (
        undefined
      )}
      <Body>
        <Section>
          <H3>{vt.section1.title}</H3>
          <div>{projectAuthorName}</div>
        </Section>

        <hr />

        <Section>
          <H3>{vt.section2.title}</H3>
          <Description>{projectImagesSummaryDescription}</Description>
          <MuiButton
            label={vt.section2.goToProjectImageList}
            color='primary'
            onClick={onEditProjectImagesClick}
            disabled={projectIsApproved}
          />
        </Section>

        <hr />

        <Section>
          <H3>{vt.section3.title}</H3>
          <Description>{vt.section3.countResultDescription}</Description>
          <CountResultWrapper>
            <CountResultShrinkWrapper>
              <CountResult projectSummary={projectSummary} />
            </CountResultShrinkWrapper>
          </CountResultWrapper>
        </Section>

        <hr />

        <Section>
          <H3>{vt.section4.title}</H3>
          {!projectIsApproved ? (
            <>
              <Description>
                {vt.section4.downloadResultImageDescription.map((s, idx) => (
                  <div key={idx}>{s}</div>
                ))}
              </Description>
              <ButtonListWrapper>
                <div
                  title={
                    noImageRegistered
                      ? vt.section4.tooltip.noImageMessageForApproveButton
                      : !projectIsApprovable
                      ? vt.section4.tooltip
                          .countingNotCompletedMessageForApproveButton
                      : ''
                  }
                >
                  <MuiButton
                    label={vt.section4.buttons.approve}
                    color='primary'
                    disabled={!projectIsApprovable}
                    onClick={noImageRegistered ? noop : onApproveResultClick}
                  />
                </div>
              </ButtonListWrapper>
            </>
          ) : (
            <div>
              <ButtonListWrapper>
                <MuiButton
                  label={vt.section4.buttons.unapprove}
                  color='primary'
                  onClick={onUnapproveClick}
                />
              </ButtonListWrapper>
              <ButtonListWrapper>
                <div
                  title={
                    noImageRegistered
                      ? vt.section4.tooltip.noImageMessageForDownloadButton
                      : ''
                  }
                >
                  <MuiButton
                    label={vt.section4.buttons.downloadImage}
                    color='primary'
                    disabled={noImageRegistered}
                    onClick={noImageRegistered ? noop : onDownloadPdfClick}
                  />
                </div>
                <div
                  title={
                    noImageRegistered
                      ? vt.section4.tooltip.noImageMessageForDownloadButton
                      : ''
                  }
                >
                  <MuiButton
                    label={vt.section4.buttons.downloadCsv}
                    color='primary'
                    disabled={noImageRegistered}
                    onClick={noImageRegistered ? noop : onDownloadCsvFileClick}
                  />
                </div>
              </ButtonListWrapper>
            </div>
          )}
        </Section>
      </Body>
    </Root>
  )
);

ProjectDetailView.displayName = 'ProjectDetailView';
